import { CopyableValue } from "@/components/CopyableValue";
import { DataTable } from "@/components/DataTable";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  api,
  FEEDBACK_COMMENT_MAX,
  type AdminFeedbackReportRow,
  type FeedbackManifest,
  type FeedbackReview,
  type FeedbackReviewUpdate,
} from "@/lib/api";
import { useSession } from "@/lib/auth-client";
import { formatCost, formatTokens, relativeIso } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@tanstack/react-router";
import {
  Archive,
  ArchiveRestore,
  Check,
  ChevronDown,
  CircleHelp,
  Copy,
  Download,
  KeyRound,
  Terminal,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

// One Boo's runtime-feedback report table and its detail sheet, rendered by
// the Feedback tab on the admin container detail page. Rows are the
// summarized shape boo-backend serves; the sheet fetches the full manifest
// on open, downloads stream the brain's tar.gz through boo-backend, and
// archive/reopen writes the brain's review block through it too.
// Super-admin only — packages hold the customer conversation for that turn.

// ── Vocabulary ───────────────────────────────────────────────────

const CATEGORY_LABEL: Record<string, string> = {
  tool: "Tool",
  skill: "Skill",
  scheduled_task: "Scheduled task",
  automation: "Automation",
  sandbox: "Working space",
  permissions: "Permissions",
  reasoning: "Reasoning",
  other: "Other",
};

const CATEGORY_TONE: Record<string, string> = {
  tool: "border-sky-500/40 text-sky-700 dark:text-sky-300",
  skill: "border-violet-500/40 text-violet-700 dark:text-violet-300",
  scheduled_task: "border-amber-500/40 text-amber-700 dark:text-amber-300",
  automation: "border-teal-500/40 text-teal-700 dark:text-teal-300",
  sandbox: "border-orange-500/40 text-orange-700 dark:text-orange-300",
  permissions: "border-rose-500/40 text-rose-700 dark:text-rose-300",
  reasoning: "border-indigo-500/40 text-indigo-700 dark:text-indigo-300",
};

const NO_REPORTS_TEXT =
  "No reports yet. A Boo files one when its runtime feedback is switched on and something in its own runtime misbehaves.";

// The boo-brain analysis skill (.claude/skills/analyze-boo-feedback-report),
// a Claude Code slash command; positional container id then report id.
const ANALYZE_COMMAND = "/analyze-boo-feedback-report";
const analyzeCommand = (row: AdminFeedbackReportRow) =>
  `${ANALYZE_COMMAND} ${row.container.id} ${row.reportId}`;
// The skill reads these from the boo-brain checkout's .env (or Claude Code's
// settings.local.json env block) and defaults the backend to production, so
// the URL line only appears when this dashboard talks to a different backend.
const PROD_BACKEND_URL = "https://backend.boolab.ai";
const setupEnvLines = (key: string) =>
  [
    `BOO_AUTOMATION_KEY=${key}`,
    ...(import.meta.env.VITE_API_URL === PROD_BACKEND_URL
      ? []
      : [`BOO_BACKEND_URL=${import.meta.env.VITE_API_URL}`]),
  ].join("\n");
// The longest expiry the backend allows for a key that can read reports.
const KEY_EXPIRY_DAYS = 90;

function CategoryBadge({ category }: { category: string }) {
  return (
    <Badge variant="outline" className={cn("whitespace-nowrap", CATEGORY_TONE[category])}>
      {CATEGORY_LABEL[category] ?? category}
    </Badge>
  );
}

function absoluteIso(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Copy ─────────────────────────────────────────────────────────

async function copyText(text: string, label: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    // navigator.clipboard needs a secure context — the value stays
    // select-all for manual copy rather than failing loudly.
    toast.error("Couldn't copy — select the text instead");
  }
}

// Inline monospace value that copies itself on click; the sheet title and the
// table's id column use it so a report id never has to be retyped.
function CopyChip({ value, label, className }: { value: string; label: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={`Copy ${label.toLowerCase()}`}
      onClick={(e) => {
        e.stopPropagation();
        void copyText(value, label).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-xs transition-colors hover:bg-muted hover:text-foreground",
        className,
      )}
    >
      <span className="select-all">{value}</span>
      {copied ? (
        <Check className="size-3 text-emerald-600" />
      ) : (
        <Copy className="size-3 text-muted-foreground" />
      )}
    </button>
  );
}

// Row actions are icon-only with a short tooltip; the sheet spells them out.
function IconAction({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

// Setup lives behind a help icon: the one-time .env line(s), with the key
// minted in place. The minted key is held here (not in the popover content,
// which unmounts on close) so it survives reopening while the sheet is up.
function AnalyzeSetupHelp() {
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const [minted, setMinted] = useState<string | null>(null);
  const mint = useMutation({
    mutationFn: () =>
      api.admin.createAgentKey({
        label: `Feedback analysis · ${session?.user?.email ?? "super admin"}`,
        capabilities: ["feedback:read"],
        expiresAt: new Date(Date.now() + KEY_EXPIRY_DAYS * 86_400_000).toISOString(),
      }),
    onSuccess: (res) => {
      setMinted(res.data.key);
      queryClient.invalidateQueries({ queryKey: ["admin-agent-keys"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't create the key"),
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="How to run the prompt">
          <CircleHelp className="size-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-2 text-xs">
        <p>Run the prompt in Claude Code, inside a boo-brain checkout.</p>
        <p>
          Once, add to <span className="font-mono">boo-brain/.env</span>:
        </p>
        <CopyableValue value={setupEnvLines(minted ?? "<your key>")} />
        {minted ? (
          <p className="text-muted-foreground">
            Key shown once. Read-only, expires in {KEY_EXPIRY_DAYS} days. Revoke it on the{" "}
            <Link to="/superadmin/agent-keys" className="text-primary underline-offset-4 hover:underline">
              API keys
            </Link>{" "}
            page.
          </p>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={mint.isPending} onClick={() => mint.mutate()}>
              <KeyRound className="size-3.5" />
              {mint.isPending ? "Creating…" : "Generate key"}
            </Button>
            <span className="text-muted-foreground">Read-only. Expires in {KEY_EXPIRY_DAYS} days.</span>
          </div>
        )}
        <p className="text-muted-foreground">
          Same lines work in <span className="font-mono">.claude/settings.local.json</span> under{" "}
          <span className="font-mono">env</span>.
        </p>
      </PopoverContent>
    </Popover>
  );
}

// ── Download ─────────────────────────────────────────────────────

// The archive route is cross-origin and cookie-authenticated, so a plain
// anchor cannot carry the session or surface a 4xx. Fetch it as a blob and
// hand the browser an object URL; packages are small (tens to hundreds of KB).
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function useArchiveDownload() {
  return useMutation({
    mutationFn: async ({ containerId, reportId }: { containerId: string; reportId: string }) => {
      const blob = await api.admin.downloadFeedbackArchive(containerId, reportId);
      saveBlob(blob, `${reportId}.tar.gz`);
      return reportId;
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Download failed");
    },
  });
}

function DownloadButton({
  pending,
  onClick,
  size = "sm",
}: {
  pending: boolean;
  onClick: () => void;
  size?: "sm" | "default";
}) {
  return (
    <Button
      variant="outline"
      size={size}
      disabled={pending}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <Download className="size-4" />
      {pending ? "Preparing…" : "Download"}
    </Button>
  );
}

// ── Review (archive / reopen) ────────────────────────────────────

// One mutation for the row action and the sheet. The brain answers the
// updated manifest, which replaces the sheet's copy in place; every filter
// of this Boo's list is refetched so the row moves between Open and Archived.
function useReviewMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      containerId,
      reportId,
      update,
    }: {
      containerId: string;
      reportId: string;
      update: FeedbackReviewUpdate;
    }) => api.admin.setFeedbackReview(containerId, reportId, update),
    onSuccess: (manifest, { containerId, reportId, update }) => {
      queryClient.setQueryData(["admin-feedback-manifest", containerId, reportId], manifest);
      void queryClient.invalidateQueries({ queryKey: ["admin-container-feedback", containerId] });
      toast.success(update.state === "archived" ? "Report archived" : "Report reopened");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Couldn't update the report");
    },
  });
}

const reviewLine = (review: FeedbackReview) =>
  `Archived ${relativeIso(review.at)}${review.by ? ` by ${review.by}` : ""}`;

function ArchivedBadge({ review }: { review: FeedbackReview }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="secondary" className="gap-1 whitespace-nowrap">
          <Archive className="size-3" />
          Archived
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        <p>{reviewLine(review)}</p>
        {review.comment && <p className="mt-1 whitespace-pre-wrap opacity-80">{review.comment}</p>}
      </TooltipContent>
    </Tooltip>
  );
}

// The sheet's verdict block: for an archived report, who closed it, the note,
// and the way back; for an open one, the archive form (a note is optional —
// archiving without one is a plain dismiss).
function ReviewPanel({
  review,
  archiveOpen,
  pending,
  onSubmit,
}: {
  review: FeedbackReview | null;
  archiveOpen: boolean;
  pending: boolean;
  onSubmit: (update: FeedbackReviewUpdate) => void;
}) {
  const [open, setOpen] = useState(archiveOpen);
  const [comment, setComment] = useState("");

  if (review) {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/30 p-3 text-sm">
        <div className="flex items-center gap-2">
          <Archive className="size-4 shrink-0 text-muted-foreground" />
          <span className="font-medium" title={absoluteIso(review.at)}>
            {reviewLine(review)}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            disabled={pending}
            onClick={() => onSubmit({ state: "open" })}
          >
            <ArchiveRestore className="size-4" />
            {pending ? "Reopening…" : "Reopen"}
          </Button>
        </div>
        <p className={cn("whitespace-pre-wrap", !review.comment && "text-muted-foreground")}>
          {review.comment || "No note was left."}
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <div>
        <Button variant="outline" onClick={() => setOpen(true)}>
          <Archive className="size-4" />
          Archive
        </Button>
      </div>
    );
  }

  const trimmed = comment.trim();
  return (
    <form
      className="flex flex-col gap-2 rounded-md border border-border p-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(trimmed ? { state: "archived", comment: trimmed } : { state: "archived" });
      }}
    >
      <Label htmlFor="feedback-archive-note">Why is this report closed? (optional)</Label>
      <Textarea
        id="feedback-archive-note"
        autoFocus
        value={comment}
        maxLength={FEEDBACK_COMMENT_MAX}
        placeholder="Fixed in 0.32 · duplicate of another report · works as designed"
        onChange={(e) => setComment(e.target.value)}
        className="min-h-20"
      />
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={pending}>
          <Archive className="size-4" />
          {pending ? "Archiving…" : "Archive report"}
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {comment.length}/{FEEDBACK_COMMENT_MAX}
        </span>
      </div>
    </form>
  );
}

// ── Table ────────────────────────────────────────────────────────

type Selection = { row: AdminFeedbackReportRow; archive: boolean };

/** Sortable report table with per-row download, archive/reopen, and a click-through detail sheet. */
export function FeedbackReportsTable({
  rows,
  emptyText = NO_REPORTS_TEXT,
}: {
  rows: AdminFeedbackReportRow[];
  emptyText?: string;
}) {
  const download = useArchiveDownload();
  const startDownload = download.mutate;
  const pendingReportId = download.isPending ? download.variables?.reportId : undefined;
  const review = useReviewMutation();
  const startReview = review.mutate;
  const reviewingReportId = review.isPending ? review.variables?.reportId : undefined;
  // `archive` opens the sheet straight onto the archive form (the row action).
  const [selected, setSelected] = useState<Selection | null>(null);
  // Only a list that holds archived rows (the Archived and All filters) earns
  // a status column; the Open filter never shows one.
  const hasArchived = rows.some((r) => r.review != null);

  const columns = useMemo<ColumnDef<AdminFeedbackReportRow>[]>(
    () => [
      {
        accessorKey: "filedAt",
        header: "Filed",
        cell: ({ getValue }) => {
          const iso = getValue() as string;
          return (
            <time dateTime={iso} title={absoluteIso(iso)} className="whitespace-nowrap text-muted-foreground">
              {relativeIso(iso)}
            </time>
          );
        },
      },
      {
        accessorKey: "reportId",
        header: "Report",
        enableSorting: false,
        cell: ({ getValue }) => <CopyChip value={getValue() as string} label="Report id" />,
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ getValue }) => <CategoryBadge category={getValue() as string} />,
      },
      ...(hasArchived
        ? [
            {
              id: "status",
              accessorFn: (r) => (r.review ? 1 : 0),
              header: "Status",
              cell: ({ row }) =>
                row.original.review ? (
                  <ArchivedBadge review={row.original.review} />
                ) : (
                  <span className="text-xs text-muted-foreground">Open</span>
                ),
            } satisfies ColumnDef<AdminFeedbackReportRow>,
          ]
        : []),
      {
        accessorKey: "summary",
        header: "Summary",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="max-w-md">
            <p className="line-clamp-2 text-sm">{row.original.summary || "—"}</p>
            {row.original.reportCount > 1 && (
              <span className="text-xs text-muted-foreground">
                +{row.original.reportCount - 1} more in this turn
              </span>
            )}
          </div>
        ),
      },
      {
        id: "where",
        accessorFn: (r) => `${r.thread.kind} ${r.thread.provider}`,
        header: "Where",
        cell: ({ row }) => {
          const t = row.original.thread;
          return (
            <div className="flex min-w-0 flex-col text-xs">
              <span>
                <span className="capitalize">{t.kind}</span>
                <span className="text-muted-foreground"> · {t.provider || "—"}</span>
              </span>
              {t.channelId && (
                <span className="truncate font-mono text-muted-foreground">{t.channelId}</span>
              )}
            </div>
          );
        },
      },
      {
        id: "version",
        accessorFn: (r) => r.brain.version,
        header: "Version",
        cell: ({ row }) => (
          <div className="flex flex-col text-xs">
            <span className="font-mono">{row.original.brain.version}</span>
            <span className="font-mono text-muted-foreground" title={row.original.brain.gitSha}>
              {row.original.brain.gitSha === "unknown"
                ? "sha unknown"
                : row.original.brain.gitSha.slice(0, 7)}
            </span>
          </div>
        ),
      },
      {
        id: "cost",
        accessorFn: (r) => r.turn.costUsd ?? -1,
        header: "Cost",
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.turn.costUsd == null ? "—" : formatCost(row.original.turn.costUsd)}
          </span>
        ),
      },
      {
        id: "events",
        accessorFn: (r) => r.capture.events,
        header: "Events",
        cell: ({ row }) => <span className="tabular-nums">{row.original.capture.events}</span>,
      },
      {
        id: "download",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <IconAction
              label="Copy prompt"
              onClick={() => void copyText(analyzeCommand(row.original), "Prompt")}
            >
              <Terminal className="size-4" />
            </IconAction>
            <IconAction
              label="Download package"
              disabled={pendingReportId === row.original.reportId}
              onClick={() =>
                startDownload({
                  containerId: row.original.container.id,
                  reportId: row.original.reportId,
                })
              }
            >
              <Download className="size-4" />
            </IconAction>
            {row.original.review ? (
              <IconAction
                label="Reopen"
                disabled={reviewingReportId === row.original.reportId}
                onClick={() =>
                  startReview({
                    containerId: row.original.container.id,
                    reportId: row.original.reportId,
                    update: { state: "open" },
                  })
                }
              >
                <ArchiveRestore className="size-4" />
              </IconAction>
            ) : (
              <IconAction
                label="Archive…"
                onClick={() => setSelected({ row: row.original, archive: true })}
              >
                <Archive className="size-4" />
              </IconAction>
            )}
          </div>
        ),
      },
    ],
    [startDownload, pendingReportId, startReview, reviewingReportId, hasArchived],
  );

  return (
    <>
      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed p-10 text-center text-sm text-muted-foreground">
          {emptyText}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          enableSorting
          initialSorting={[{ id: "filedAt", desc: true }]}
          onRowClick={(row) => setSelected({ row, archive: false })}
        />
      )}
      <FeedbackReportSheet
        row={selected?.row ?? null}
        archiveIntent={selected?.archive ?? false}
        onClose={() => setSelected(null)}
        downloading={selected != null && pendingReportId === selected.row.reportId}
        onDownload={() =>
          selected &&
          startDownload({ containerId: selected.row.container.id, reportId: selected.row.reportId })
        }
        reviewing={selected != null && reviewingReportId === selected.row.reportId}
        onReview={(update) =>
          selected &&
          startReview({
            containerId: selected.row.container.id,
            reportId: selected.row.reportId,
            update,
          })
        }
      />
    </>
  );
}

// ── Detail sheet ─────────────────────────────────────────────────

function Section({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group flex min-h-10 w-full items-center justify-between gap-3 text-left"
        >
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground transition-colors group-hover:text-foreground">
            {title}
          </span>
          <ChevronDown className="size-3.5 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-1 pb-4">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function Field({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  if (value == null || value === "") return null;
  return (
    <div className="grid grid-cols-[150px_1fr] items-start gap-3 py-1 text-sm">
      <span className="pt-0.5 text-xs text-muted-foreground">{label}</span>
      <span className={cn("min-w-0 break-all", mono && "font-mono text-xs")}>{value}</span>
    </div>
  );
}

function shortSha(sha: string | undefined): string | undefined {
  if (!sha || sha === "unknown") return sha;
  return sha.length > 12 ? sha.slice(0, 12) : sha;
}

function FeedbackReportSheet({
  row,
  archiveIntent,
  onClose,
  onDownload,
  downloading,
  reviewing,
  onReview,
}: {
  row: AdminFeedbackReportRow | null;
  archiveIntent: boolean;
  onClose: () => void;
  onDownload: () => void;
  downloading: boolean;
  reviewing: boolean;
  onReview: (update: FeedbackReviewUpdate) => void;
}) {
  const manifestQuery = useQuery({
    queryKey: ["admin-feedback-manifest", row?.container.id, row?.reportId],
    queryFn: () => api.admin.feedbackManifest(row!.container.id, row!.reportId),
    enabled: row != null,
    staleTime: 5 * 60 * 1000,
  });
  const m = manifestQuery.data;
  // The manifest is the fresher copy once loaded (a review replaces it in
  // the cache); the row's block only bridges the load.
  const review = m ? (m.review ?? null) : (row?.review ?? null);

  return (
    <Sheet open={row != null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl md:max-w-3xl p-6 overflow-y-auto">
        <SheetHeader className="p-0 mb-2">
          <SheetTitle className="flex items-center gap-2">
            <CategoryBadge category={row?.category ?? "other"} />
            {review && <ArchivedBadge review={review} />}
            <span>Report</span>
            {row && <CopyChip value={row.reportId} label="Report id" className="text-sm" />}
          </SheetTitle>
          <SheetDescription>
            {row && (
              <>
                Filed {absoluteIso(row.filedAt)} by {row.container.name} ·{" "}
                {row.container.organizationName}
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {row && (
          <div className="mb-4 flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <Button variant="outline" onClick={() => void copyText(analyzeCommand(row), "Prompt")}>
                <Terminal className="size-4" />
                Copy prompt
              </Button>
              <AnalyzeSetupHelp />
            </div>
            <div className="flex items-center gap-2">
              <DownloadButton pending={downloading} onClick={onDownload} size="default" />
              <span className="text-xs text-muted-foreground">
                {row.reportId}.tar.gz
                {m?.capture ? ` · ${formatBytes(m.capture.capturedBytes)} captured` : ""}
              </span>
            </div>
            <ReviewPanel
              key={`${row.reportId}:${archiveIntent}`}
              review={review}
              archiveOpen={archiveIntent}
              pending={reviewing}
              onSubmit={onReview}
            />
          </div>
        )}

        {manifestQuery.isLoading ? (
          <LoadingIndicator />
        ) : manifestQuery.isError ? (
          <InlineError error={manifestQuery.error} />
        ) : m ? (
          <ManifestBody m={m} />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function ManifestBody({ m }: { m: FeedbackManifest }) {
  const accessFlags = Object.entries(m.flags?.access ?? {});
  const threadFlags = Object.entries(m.flags?.thread ?? {});
  const catalog = Object.entries(m.catalog ?? {});
  return (
    <div className="divide-y">
      <Section title={m.reports.length === 1 ? "What Boo reported" : `What Boo reported (${m.reports.length})`} defaultOpen>
        {m.reports.map((r, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-md border border-border p-3 text-sm">
            <div className="flex items-center gap-2">
              <CategoryBadge category={r.category} />
              <time dateTime={r.filedAt} className="text-xs text-muted-foreground">
                {absoluteIso(r.filedAt)}
              </time>
            </div>
            <p>{r.summary}</p>
            {r.expected && (
              <p className="text-xs">
                <span className="font-medium text-muted-foreground">Expected · </span>
                {r.expected}
              </p>
            )}
            {r.observed && (
              <p className="text-xs">
                <span className="font-medium text-muted-foreground">Observed · </span>
                {r.observed}
              </p>
            )}
            {r.suspects && r.suspects.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-xs text-muted-foreground">Suspects:</span>
                {r.suspects.map((s) => (
                  <Badge key={s} variant="secondary" className="font-mono text-[10px]">
                    {s}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        ))}
      </Section>

      <Section title="Turn" defaultOpen>
        <Field label="Kind" value={m.turn.turnKind} />
        <Field label="Outcome" value={m.turn.outcome} />
        <Field label="Model" value={m.turn.modelSelection?.modelName} />
        <Field
          label="Model source"
          value={
            m.turn.modelSelection
              ? `${m.turn.modelSelection.purpose} · ${m.turn.modelSelection.source}`
              : undefined
          }
        />
        <Field label="Duration" value={formatDuration(m.turn.durationMs)} mono={false} />
        <Field
          label="Cost"
          value={m.turn.costUsd == null ? "—" : formatCost(m.turn.costUsd)}
          mono={false}
        />
        <Field
          label="Tokens"
          value={m.turn.tokens == null ? "—" : formatTokens(m.turn.tokens)}
          mono={false}
        />
        <Field label="Turn id" value={m.turn.turnId} />
      </Section>

      <Section title="Where" defaultOpen>
        <Field label="Kind" value={m.thread.kind} />
        <Field label="Provider" value={m.thread.provider} />
        <Field label="Channel" value={m.thread.channelId} />
        <Field label="Provider thread" value={m.thread.providerThreadId} />
        <Field label="Thread id" value={m.thread.threadId} />
        <Field label="Heartbeat task" value={m.thread.heartbeatTaskId} />
        <Field label="Daemon" value={m.thread.daemonId} />
      </Section>

      <Section title="Boo build">
        <Field label="Version" value={m.brain.version} />
        <Field label="Git sha" value={m.brain.gitSha} />
        <Field label="Built at" value={m.brain.builtAt} />
        <Field label="Instance" value={m.brain.instanceId} />
        <Field label="Container id" value={m.brain.containerId} />
        <Field label="Filed at" value={m.filedAt} />
        <Field label="Sealed at" value={m.sealedAt} />
      </Section>

      {m.sandbox && (
        <Section title="Working space">
          <Field label="Kind" value={m.sandbox.kind} />
          <Field label="Template" value={m.sandbox.template} />
          <Field label="Sandbox id" value={m.sandbox.sandboxId} />
        </Section>
      )}

      {(accessFlags.length > 0 || threadFlags.length > 0) && (
        <Section title="Flags">
          {accessFlags.map(([id, cfg]) => (
            <Field
              key={id}
              label={id}
              value={`${cfg.access} · ${cfg.approvers === "everyone" ? "everyone" : `${cfg.approvers.length} approver${cfg.approvers.length === 1 ? "" : "s"}`}`}
            />
          ))}
          {threadFlags.map(([id, cfg]) => (
            <Field
              key={`thread-${id}`}
              label={`${id} (this thread)`}
              value={[cfg.model, cfg.enabledBy && `by ${cfg.enabledBy}`, cfg.enabledAtIso]
                .filter(Boolean)
                .join(" · ")}
            />
          ))}
        </Section>
      )}

      {m.skills && (
        <Section title={`Skills (${m.skills.activated.length} activated)`}>
          {m.skills.activated.length === 0 && (
            <p className="text-xs text-muted-foreground">No skills were activated in this turn.</p>
          )}
          {m.skills.activated.map((s) => (
            <Field
              key={s.ref}
              label={`${s.namespace}/${s.name}`}
              value={`${s.ref} · snapshot ${s.snapshot} · ${s.files} file${s.files === 1 ? "" : "s"}`}
            />
          ))}
          {m.skills.unresolved.length > 0 && (
            <Field
              label="Unresolved"
              value={JSON.stringify(m.skills.unresolved)}
            />
          )}
        </Section>
      )}

      {catalog.length > 0 && (
        <Section title="Catalogs">
          {catalog.map(([system, info]) => (
            <Field
              key={system}
              label={system}
              value={[shortSha(info.headSha) ?? "no head", info.files != null && `${info.files} files`]
                .filter(Boolean)
                .join(" · ")}
            />
          ))}
        </Section>
      )}

      {m.bundles && m.bundles.length > 0 && (
        <Section title="Bundles">
          {m.bundles.map((b) => (
            <Field
              key={b.id}
              label={b.id}
              value={[b.version, shortSha(b.digest), b.completedDigest && `completed ${shortSha(b.completedDigest)}`]
                .filter(Boolean)
                .join(" · ") || "—"}
            />
          ))}
        </Section>
      )}

      {m.experiments && (
        <Section title="Experiments">
          <Field label="Configured" value={m.experiments.configured ? "yes" : "no"} mono={false} />
          <Field label="Distinct id" value={m.experiments.distinctId} />
          <Field label="Last error" value={m.experiments.lastError} />
          {m.experiments.resolutions.map((r) => (
            <Field key={r.id} label={r.id} value={`${JSON.stringify(r.value)} · ${r.source}`} />
          ))}
        </Section>
      )}

      {m.capture && (
        <Section title="Capture">
          <Field label="Events" value={String(m.capture.events)} mono={false} />
          <Field label="Messages" value={String(m.capture.messages)} mono={false} />
          <Field label="Captured" value={formatBytes(m.capture.capturedBytes)} mono={false} />
          <Field label="Dropped messages" value={String(m.capture.droppedMessages)} mono={false} />
          {m.capture.skippedFiles.length > 0 && (
            <Field label="Skipped files" value={m.capture.skippedFiles.join("\n")} />
          )}
          {m.usage && <Field label="Usage rows" value={String(m.usage.rows)} mono={false} />}
        </Section>
      )}

      {m.archive && (
        <Section title={`Archive contents (${m.archive.entries.length})`}>
          <pre className="max-h-64 overflow-auto rounded-md border border-border bg-muted/40 p-3 text-[11px] leading-relaxed">
            {m.archive.entries.join("\n")}
          </pre>
        </Section>
      )}

      <Section title="Raw manifest">
        <CopyableValue value={JSON.stringify(m, null, 2)} className="max-h-96 overflow-auto" />
      </Section>
    </div>
  );
}
